import { FC } from "react";
import { Box, Typography } from "@mui/material";
import { makeSxStyles } from "ui";
import { useAuthStore } from "service";
import useImpersonation from "../../../features/organizations/hooks/useImpersonation";
import { IMPERSONATION_BANNER_HEIGHT } from "../../../constants/layout";

/**
 * Full-width strip rendered once by Layout while a superadmin is
 * impersonating an organization. Self-contained - reads isImpersonating from
 * the store itself and returns null otherwise, so Layout can render it
 * unconditionally.
 */
const ImpersonationBanner: FC = () => {
  const styles = useStyles();
  const { isImpersonating, organization } = useAuthStore();
  const { exit, isExiting } = useImpersonation();

  if (!isImpersonating) {
    return null;
  }

  return (
    <Box
      sx={{
        ...styles.container,
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
      data-testid="impersonation-banner"
    >
      <Typography sx={styles.breadcrumb} onClick={isExiting ? undefined : exit}>
        Manage organizations{" "}
        <Box component="span" sx={styles.separator}>
          &rsaquo;
        </Box>{" "}
        {organization?.name ?? "Organization"}
      </Typography>
      <Typography
        sx={styles.exitLink}
        onClick={isExiting ? undefined : exit}
        data-testid="exit-organization"
      >
        {isExiting ? "Exiting..." : "Exit Organization"}
      </Typography>
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  container: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: `${IMPERSONATION_BANNER_HEIGHT}px`,
    boxSizing: "border-box",
    padding: "8px 40px 8px 24px",
    background: "#004B6F",
  },
  breadcrumb: {
    fontFamily: "Inter",
    fontSize: "13px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    color: "#C9E6FF",
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },
  separator: {
    display: "inline",
    color: "#C9E6FF",
  },
  exitLink: {
    fontFamily: "Inter",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: "20px",
    letterSpacing: "0.1px",
    color: "#C9E6FF",
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },
}));

export default ImpersonationBanner;
